import { banco } from '../db/banco'
import type { Meta, Pedido, SubPedido } from '../db/tipos'
import { arredondar2 } from '../pedidos/precos'
import { valorEfetivo } from '../comissoes/motor'
import { dentroDoPeriodo } from './calculo'
import { STATUS_VENDA } from './statusVenda'


async function vendasDoPeriodo(meta: Meta): Promise<Array<{ subPedido: SubPedido; pedido: Pedido }>> {
  const pedidos = await banco.pedidos
    .filter(
      (p) =>
        p.apagado_em === null &&
        p.status === 'fechado' &&
        dentroDoPeriodo(p.data, meta.periodo_inicio, meta.periodo_fim),
    )
    .toArray()
  const resultado: Array<{ subPedido: SubPedido; pedido: Pedido }> = []
  for (const pedido of pedidos) {
    const subPedidos = await banco.sub_pedidos
      .where('pedido_id')
      .equals(pedido.id)
      .filter((s) => s.apagado_em === null && STATUS_VENDA.includes(s.status))
      .toArray()
    for (const subPedido of subPedidos) resultado.push({ subPedido, pedido })
  }
  return resultado
}

function representadaDoEscopo(escopo: string): string | null {
  return escopo.startsWith('representada:') ? escopo.slice('representada:'.length) : null
}

export async function realizadoDaMeta(meta: Meta, _hoje: Date): Promise<number> {
  const representadaId = representadaDoEscopo(meta.escopo)
  const vendas = (await vendasDoPeriodo(meta)).filter(
    (v) => representadaId === null || v.subPedido.representada_id === representadaId,
  )

  switch (meta.tipo) {
    case 'valor_vendido':
    case 'faturamento_total':
      return arredondar2(vendas.reduce((soma, v) => soma + v.subPedido.valor_total, 0))

    case 'positivacao':
      return new Set(vendas.map((v) => v.pedido.cliente_id)).size

    case 'itens_por_pedido': {
      const idsPedidos = new Set(vendas.map((v) => v.pedido.id))
      if (idsPedidos.size === 0) return 0
      let totalItens = 0
      for (const venda of vendas) {
        totalItens += await banco.itens_pedido
          .where('sub_pedido_id')
          .equals(venda.subPedido.id)
          .filter((i) => i.apagado_em === null)
          .count()
      }
      return arredondar2(totalItens / idsPedidos.size)
    }

    case 'cobertura_linhas': {
      if (!representadaId) return 0
      const produtosDaRepresentada = await banco.produtos
        .where('representada_id')
        .equals(representadaId)
        .filter((p) => p.apagado_em === null && p.ativo && p.linha !== null)
        .toArray()
      const linhasExistentes = new Set(produtosDaRepresentada.map((p) => p.linha))
      if (linhasExistentes.size === 0) return 0
      const linhasVendidas = new Set<string>()
      for (const venda of vendas) {
        const itens = await banco.itens_pedido
          .where('sub_pedido_id')
          .equals(venda.subPedido.id)
          .filter((i) => i.apagado_em === null)
          .toArray()
        for (const item of itens) {
          const produto = await banco.produtos.get(item.produto_id)
          if (produto?.linha && linhasExistentes.has(produto.linha)) {
            linhasVendidas.add(produto.linha)
          }
        }
      }
      return arredondar2((linhasVendidas.size / linhasExistentes.size) * 100)
    }

    case 'comissao_necessaria': {
      let total = 0
      for (const venda of vendas) {
        const lancamento = await banco.lancamentos_comissao
          .where('sub_pedido_id')
          .equals(venda.subPedido.id)
          .filter((l) => l.apagado_em === null)
          .first()
        if (lancamento) total += valorEfetivo(lancamento)
      }
      return arredondar2(total)
    }
  }
}
